import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { FormViewService } from '@xilo-mono/form-contracts';
import { get } from 'lodash';
import { combineLatest, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
export type OperatorTypes =
  | 'contains'
  | 'is not null'
  | 'is null'
  | 'not in'
  | 'in'
  | '!='
  | '>'
  | '<'
  | '='
  | '<='
  | '>='
  | '><'
  | 'add'
  | 'subtract'
  | 'multiply'
  | 'divide';
export interface Calculation {
  condition: 'and' | 'or';
  rules: CaclulatorRules[];
  showWhenTrue: boolean;
}
export interface CaclulatorRules {
  operator: OperatorTypes;
  value: any;
  field: string;
  parents: string[];
  display?: string | number;
}
@Component({
  selector: 'xilo-mono-form-viewer-calculator',
  templateUrl: './calculator.component.html',
  styleUrls: [
    '../../form-viewer.component.scss',
    './calculator.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomCalculatorComponent extends FieldType implements OnInit, OnDestroy {
  isInvalid = false;
  errorMessage = false;
  display: string | number;
  optionOne = 'Operations on one Field';
  optionTwo = 'Operations with Other fields';
  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    const relatedFields = new Set<string>();
    
    this.field?.templateOptions?.calculation?.rules?.forEach(rule => {
      relatedFields.add(rule.field);
    });

    const formControls = Array.from(relatedFields)
      .map(fieldName => this.field.parent.formControl.get(fieldName))
      .filter((control): control is NonNullable<typeof control> => control !== null);

    if (formControls.length > 0) {
      combineLatest(
        formControls.map(control => 
          control.valueChanges.pipe(
            startWith(control.value),
            debounceTime(500),
            distinctUntilChanged(),
            takeUntil(this.destroyed$)
          )
        )
      ).pipe(
        takeUntil(this.destroyed$)
      ).subscribe(() => {
        this.setDisplayValue();
        this.cdr.detectChanges();
      });
    }

    this.setDisplayValue();
}

    ngOnDestroy() {
      this.destroyed$.next(true);
      this.destroyed$.complete();
    }

  styleText() {
    const styleObj = {};
    const styleParams = ['fontSize', 'fontWeight', 'color', 'textAlign'];
    for (const key of styleParams) {
      if (this.to[key]) {
        styleObj[key] = this.to[key];
      }
    }
    return styleObj;
  }

  styleCalcVal() {
    const styleObj = {};
    const styleParams = ['fontSize', 'color', 'textAlign'];
    for (const key of styleParams) {
      if (this.to[key]) {
        styleObj[key] = this.to[key];
      }
    }
    return styleObj;
  }

  getError() {
    return 'Invalid response';
  }

  setDisplayValue() {
    if (this.field?.templateOptions?.calculation && this.model) {
      this.display = this.applyRules(this.field?.templateOptions?.calculation)
      this.cdr.detectChanges();
    }
  }

  applyRules(calc) {
    const rules = calc.rules || [];
  
    if (calc.calcOptions == this.optionOne) {
      let result = 0;
      for (const q of rules) {
        const value = Number(this.getValues(q.field, q.parents));
        result = this.mathOperations(q, value);
        result = this.formatDecimal(result); 
        this.formControl.setValue(result);
        q.display = result  
        return q.display
      }

    } else {
      let result;
      for (const [index, q] of rules.entries()) {
        const value = Number(this.getValues(q.field, q.parents));
        if (index === 0 || (result === undefined && (q.operator === 'multiply' || q.operator === 'divide'))) {
          result = value;
        } else {
          result = this.operationOnOtherFields(result, q, value);
        }
        result = this.formatDecimal(result); 
        q.display = result;
      }
      this.formControl.setValue(result);
      return result;
    }
  }

  formatDecimal(value: number): number {
    if (value && value % 1 !== 0) { 
      return parseFloat(value.toFixed(2));
    } else {
      return value;
    }
  }
  
  mathOperations(rule, value) {
    switch (rule.operator) {
      case 'add':
        return value + rule.value
      case 'subtract':
        return value - rule.value
      case 'multiply':
        return value * rule.value
      case 'divide':
        return value / rule.value
    }
  }

  operationOnOtherFields(curr, rule, value) {
    switch (rule.operator) {
      case 'add':
        curr = curr + value
        return curr
      case 'subtract':
        curr = curr - value
        return curr
      case 'multiply':
        curr = curr * value
        return curr
      case 'divide':
        curr = curr / value
        return curr
    }
  }

  getValues(key: string, parents: string[]) {
    const parentsCopy = [...parents];
    if (this.model) {
      let valueObj = { ...this.model };
      const parentObj = get(valueObj, parentsCopy[0]);
      if (Array.isArray(parentObj)) {
        valueObj = parentObj[0];
      }
      return valueObj[key];
    }
    return [];
  }

}
